import { Inject, Injectable } from '@angular/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ReviewsService } from './reviews.service';
import {
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  OrganizationalContainer,
  TOKENS as MASTERDATA_TOKENS,
  UserFlowService,
} from '@trustedshops/tswp-core-masterdata';
import { ChannelsService } from './channels.service';
import { HttpParams } from '@angular/common/http';

/**
 * @alias UserFlowAssociationService
 * @description Service which links User with partial Channel data in the UserFlow
 * More details by the following link https://docs.userflow.com/docs/guides/companies
 * In our case, the Company entity in UserFlow represents the Channel associated with the b2b Account in Trusted Shops.
 */
@Injectable()
export class UserFlowAssociationService {
  static readonly TYPE = 'UserFlowAssociationService';

  constructor(
    private reviewsService: ReviewsService,
    private readonly channelsService: ChannelsService,
    @Inject(COMMON_TOKENS.LogService) private logService: LogService,
    @Inject(MASTERDATA_TOKENS.UserFlowService)
    private readonly userFlowService: UserFlowService
  ) {}

  public setupChannel(
    accountId: string,
    channel: OrganizationalContainer<{
      createdAt: Date;
    }>
  ): Observable<void> {
    return combineLatest([
      this.reviewsService.getMonthlyReviewsCountGoal(
        new HttpParams({
          fromObject: {
            channelId: channel.id,
            accountId,
          },
        })
      ),
      this.reviewsService.getReviewsCount(
        new HttpParams({
          fromObject: { channels: channel.id },
        })
      ),
      this.channelsService.selectedChannelHasSeoSettingsSet$,
      this.channelsService.selectedChannelHasInvitesReminderSettingsEnabled$,
    ]).pipe(
      map(
        ([
          getMonthlyReviewsCountGoalResponse,
          getReviewsCountResponse,
          hasSeoSettingsSet,
          hasInvitesReminderSettingsEnabled,
        ]) => {
          const requestBody = {
            channelId: channel.id,
            createdAt: channel?.info?.createdAt?.toISOString() ?? undefined,
            monthlyReviewCountGoal:
              getMonthlyReviewsCountGoalResponse?.value ?? null,
            hasReviews: getReviewsCountResponse?.items?.length > 0,
            hasSeoSettingsSet,
            hasInvitesReminderSettingsEnabled,
          };

          /**
           * Adds `hasSeoSettingsSet` to the request body only if it is defined.
           *
           * If `hasSeoSettingsSet` is undefined, it indicates the value was not fetched from the API,
           * possibly due to a failure or insufficient permissions.
           *
           * Including this value when it is defined ensures it is retained in the UserFlow.
           * If the value is explicitly set to `undefined`, it will be removed from the UserFlow.
           */
          if (hasSeoSettingsSet) {
            requestBody.hasSeoSettingsSet = hasSeoSettingsSet;
          }

          /**
           * Adds `hasInvitesReminderSettingsEnabled` to the request body only if it is defined.
           *
           * If `hasInvitesReminderSettingsEnabled` is undefined, it indicates the value was not fetched from the API,
           * possibly due to a failure or insufficient permissions.
           *
           * Including this value when it is defined ensures it is retained in the UserFlow.
           * If the value is explicitly set to `undefined`, it will be removed from the UserFlow.
           */
          if (hasInvitesReminderSettingsEnabled) {
            requestBody.hasInvitesReminderSettingsEnabled =
              hasInvitesReminderSettingsEnabled;
          }

          return requestBody;
        }
      ),
      switchMap(
        ({
          monthlyReviewCountGoal,
          hasReviews,
          createdAt,
          hasSeoSettingsSet,
          hasInvitesReminderSettingsEnabled,
        }) =>
          from(
            this.userFlowService.userflow.updateGroup({
              monthlyReviewCountGoal,
              createdAt,
              hasReviews,
              hasSeoSettingsSet,
              hasInvitesReminderSettingsEnabled,
            })
          )
      ),
      catchError((error) => {
        this.logService.error(
          UserFlowAssociationService.TYPE,
          new Error(`Link User with Channel failed: ${JSON.stringify(error)}`)
        );
        return of(null);
      })
    );
  }
}
